import { useLocale } from 'next-intl'

import { pushGtmEvent } from '@/src/analytics/gtm'
import { selectClevertapProfile } from '@/src/redux/slices/authSlice'
import { useAppSelector } from '@/src/redux/store'
import { getDeviceType } from '@/src/utils/api/getDeviceType'
import { getUserAuthValues } from '@/src/utils/auth'
import { getUserSelectedCurrencyCode } from '@/src/utils/currency/getCurrencyCode'
import { getPlatformName } from '@/src/utils/deviceType'
import { isUserSignedIn } from '@/src/utils/generalUtils/checkUserLogin'

interface HolidayContactFormFields {
    formName?: string
    /** The surface the reader came from, e.g. a package card's Enquire button. */
    leadSource?: string
    contactName?: string
    contactEmail?: string
    mobileCountryCode?: string
    mobileNumber?: string
    promoCodePresent?: boolean
    emailOptIn?: boolean
    contactAuthorisation?: boolean
}

interface HolidayDetailsPageView {
    packageSlug: string
    collectionSlug: string
    contacted: boolean
}

interface HolidayContactDetailsClick extends HolidayContactFormFields {
    packageSlug: string
    collectionSlug: string
}

const useHolidaysGtmEvent = () => {
    const locale = useLocale()
    const isSignedIn = isUserSignedIn()
    const { email } = getUserAuthValues() || {}
    const clevertapProfile = useAppSelector(selectClevertapProfile)
    const hostname = typeof window !== 'undefined' ? window.location.hostname : ''
    const platform = getPlatformName(getDeviceType())?.toLowerCase()

    // Same common attributes as the offers hook, with the holidays product category.
    const commonBase = {
        user_domain: hostname,
        platform,
        user_email: email ? email.toLowerCase() : 'not_informed',
        is_user_logged_in: isSignedIn ? 'yes' : 'no',
        user_language: locale,
        user_currency: getUserSelectedCurrencyCode()?.toUpperCase() || 'not_informed',
        product_category: 'holidays',
        user_phone: clevertapProfile?.Phone || 'not_informed'
    }

    /*
     * page_view_holiday_home — the /holidays landing page.
     *
     * Its own event rather than a `page_view_holiday_list` with a placeholder slug: the landing
     * page and a collection listing are different steps, and reporting both as one left GA able
     * to tell them apart only by reading `slug_name`. Same payload as the list event, including
     * that slug, so anything already keyed on it keeps working.
     */
    const pushHolidayHomePageViewEvent = () => {
        pushGtmEvent({ event: 'page_view_holiday_home', ...commonBase, slug_name: 'holidays' })
    }

    // page_view_holiday_list — a holiday's package listing; slug_name = listing slug.
    const pushHolidayListPageViewEvent = (slugName: string) => {
        pushGtmEvent({ event: 'page_view_holiday_list', ...commonBase, slug_name: slugName })
    }

    // page_view_holiday_details — re-pushed with contacted: true once the enquiry succeeds.
    const pushHolidayDetailsPageViewEvent = ({ packageSlug, collectionSlug, contacted }: HolidayDetailsPageView) => {
        pushGtmEvent({
            event: 'page_view_holiday_details',
            ...commonBase,
            slug_name: packageSlug,
            collection_slug: collectionSlug,
            ...(contacted && { contacted: true })
        })
    }

    // click_contact_details — the "Contact me" button click itself, once the enquiry submit succeeds.
    const pushHolidayContactDetailsClickEvent = ({
        packageSlug,
        collectionSlug,
        formName,
        leadSource,
        contactName,
        contactEmail,
        mobileCountryCode,
        mobileNumber,
        promoCodePresent,
        emailOptIn,
        contactAuthorisation
    }: HolidayContactDetailsClick) => {
        pushGtmEvent({
            event: 'click_contact_details',
            ...commonBase,
            slug_name: packageSlug,
            collection_slug: collectionSlug,
            form_name: formName,
            lead_source: leadSource,
            contact_name: contactName,
            contact_email: contactEmail,
            mobile_country_code: mobileCountryCode,
            mobile_number: mobileNumber,
            promo_code_present: promoCodePresent,
            email_opt_in: emailOptIn,
            contact_authorisation: contactAuthorisation
        })
    }

    return {
        pushHolidayHomePageViewEvent,
        pushHolidayListPageViewEvent,
        pushHolidayDetailsPageViewEvent,
        pushHolidayContactDetailsClickEvent
    }
}

export { useHolidaysGtmEvent }
export default useHolidaysGtmEvent
